import React from 'react';
import { CreditCard, CheckCircle, XCircle, Clock } from 'lucide-react';

const RefundPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-950 to-black py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Refund <span className="text-yellow-400">Policy</span>
          </h1>
          <p className="text-gray-400 text-lg">Understand our refund process and eligibility</p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Full Refund Scenarios */}
            <section className="bg-green-500/10 border border-green-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Full Refund Eligibility</h2>
                  <p className="text-gray-300">You are eligible for a full refund in the following situations:</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-300 ml-12">
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong className="text-white">Cancellation by Indya Bungy:</strong> If we cancel your jump for any reason, you will receive a 100% refund</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong className="text-white">Adverse Weather:</strong> If your jump is cancelled due to bad weather conditions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong className="text-white">Equipment Issues:</strong> If technical or equipment problems prevent your jump</span>
                </li>
              </ul>
            </section>

            {/* Customer Cancellation */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Clock className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Customer Cancellation Policy</h2>
                  <p className="text-gray-300">Refund amount depends on when you cancel:</p>
                </div>
              </div>
              <div className="space-y-4 ml-12">
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold">15+ Days Before Jump</h3>
                    <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold">0% Fee</span>
                  </div>
                  <p className="text-gray-300 text-sm">Full refund (100% of booking amount)</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold">8-14 Days Before Jump</h3>
                    <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-bold">25% Fee</span>
                  </div>
                  <p className="text-gray-300 text-sm">75% refund of booking amount</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold">Less Than 8 Days</h3>
                    <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold">50% Fee</span>
                  </div>
                  <p className="text-gray-300 text-sm">50% refund of booking amount</p>
                </div>
              </div>
            </section>

            {/* No Refund Scenarios */}
            <section className="bg-red-500/10 border border-red-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <XCircle className="text-red-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">No Refund Situations</h2>
                  <p className="text-gray-300">Refunds will NOT be provided in these cases:</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-300 ml-12">
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span><strong className="text-white">Under Influence:</strong> If you are found under the influence of alcohol or drugs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span><strong className="text-white">Medical Disqualification:</strong> If you fail the medical screening due to undisclosed conditions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span><strong className="text-white">Non-Compliance:</strong> If you fail to follow safety instructions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span><strong className="text-white">No-Show:</strong> If you don't arrive at the scheduled time without prior notice</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span><strong className="text-white">Weight/Age:</strong> If you don't meet weight (40-110kg) or age (10-65 years) requirements</span>
                </li>
              </ul>
            </section>

            {/* Refund Process */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <CreditCard className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Refund Process</h2>
                </div>
              </div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Refunds will be processed to the original payment method</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Processing time: 5-7 business days from approval</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>You will receive a confirmation email once refund is processed</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Bank processing may take additional 3-5 days</span>
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Need Help with Refund?</h2>
              <p className="text-gray-300 mb-4">
                For refund-related queries, please contact our support team:
              </p>
              <div className="space-y-2 text-gray-300">
                <p><strong className="text-white">Email:</strong> crew.indyabungy@gmail.com</p>
                <p><strong className="text-white">Phone:</strong> 8506865393</p>
                <p><strong className="text-white">Hours:</strong> 9:00 AM - 6:00 PM (Daily)</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;